import { type SiteId } from '@solinkcorp/api-types';
import { type LocaleDefinition } from '@solinkcorp/locale';
import { Button, cn, Input } from '@solinkcorp/solink-ui';
import { ALL_LOCATIONS } from '@solinkcorp/vd-common';
import classNames from 'classnames';
import ThemeVariables from 'env/themeVariables';
import { XIcon } from 'lucide-react';
import AnalyticsLogger from 'models/AnalyticsLogger';
import React, { useCallback, useMemo, useState } from 'react';
import { FixedSizeList } from 'react-window';
import CommonUtil from 'utils/CommonUtil';
import { type VideoDiscoverySite } from 'utils/types';
import styles from './SingleLocationSelector.module.sass';

interface SingleLocationSelectorProps {
  locations: readonly VideoDiscoverySite[];
  locale: LocaleDefinition;
  selectedLocationId?: SiteId;
  onLocationSelected?: (selectedId: SiteId) => void;
  closeLocationList: () => void;
}
export default function SingleLocationSelector(props: SingleLocationSelectorProps) {
  const { closeLocationList, locale, locations, onLocationSelected, selectedLocationId } = props;
  const [searchString, setSearchString] = useState('');

  const selectLocation = useCallback(
    (locationId: SiteId) => {
      if (onLocationSelected) {
        onLocationSelected(locationId);
        const location = locations.find((location) => location.id === locationId);
        if (location) {
          AnalyticsLogger.selectEventLocation({ location });
        }
      }
      closeLocationList();
    },
    [locations, onLocationSelected, closeLocationList],
  );

  const handleSearchChange = useCallback((value = '') => {
    setSearchString(value);
  }, []);

  const filteredLocations: VideoDiscoverySite[] = useMemo(
    () => CommonUtil.searchLocation({ searchString, locations }),
    [searchString, locations],
  );

  const { locationSelectorLocationsWidth, locationSelectorLocationHeight } = ThemeVariables;

  const maxLocationListHeight = document.documentElement.clientHeight * 0.5;

  const allFilteredLocationsListHeight = filteredLocations.length * locationSelectorLocationHeight;

  const locationListHeight =
    allFilteredLocationsListHeight > maxLocationListHeight
      ? maxLocationListHeight
      : allFilteredLocationsListHeight;

  return (
    <div className={cn('tw', styles.locationSelectorWrapper)}>
      <div className={styles.locationSelectorHeader}>
        <Input
          autoComplete="off"
          className={styles.locationSearchInput}
          name="search-input"
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder={locale.general.searchLocation}
          aria-label={locale.general.searchLocation}
          value={searchString}
          data-testid="location-picker-search-input"
          autoFocus
          rightIcon={
            <Button
              type="button"
              className={cn({ hidden: !searchString })}
              size="sm"
              variant="ghost"
              onClick={() => handleSearchChange('')}
            >
              <XIcon className="size-4" />
            </Button>
          }
        />
      </div>
      <div className={styles.locationSelectorBody}>
        <FixedSizeList
          itemData={{
            locations: filteredLocations,
            selectedLocationId,
            selectLocation,
          }}
          itemCount={filteredLocations.length}
          height={locationListHeight}
          width={locationSelectorLocationsWidth}
          itemSize={locationSelectorLocationHeight}
          itemKey={(index: number) => filteredLocations?.[index].id || `${index}`}
        >
          {({ data, index, style }) => <Location data={data} index={index} style={style} />}
        </FixedSizeList>
      </div>
    </div>
  );
}

function Location({
  data,
  index,
  style,
}: {
  data: {
    locations: VideoDiscoverySite[];
    selectedLocationId?: SiteId;
    selectLocation: (id: SiteId) => void;
  };
  index: number;
  style: React.CSSProperties;
}) {
  const location = data.locations[index];
  const locationItemClass = classNames(styles.locationItem, {
    [styles.allLocation]: location.id === ALL_LOCATIONS,
    [styles.selected]: data.selectedLocationId === location.id,
  });
  return (
    <div key={location.id} className={locationItemClass} style={style}>
      <div onClick={() => data.selectLocation(location.id)}>
        <div className={styles.locationName}>{location.name}</div>
        <div className={styles.locationAddress}>{location.address}</div>
      </div>
    </div>
  );
}
